/**
 * Staged mutation shims for the flow designer's "Suggest changes" mode.
 *
 * In suggest mode the user edits the flow but nothing must touch the live
 * shared draft. The designer's edit handlers all go through React Query
 * mutations (`createNode`, `createEdge`, `insertNodeOnEdge`, …) and rely on the
 * server response only for the freshly-minted ids they back-fill over their
 * optimistic temp ids. So we wrap each mutation: while `suggestingRef.current`
 * is true, `mutateAsync` resolves locally with a synthetic response shaped like
 * the real one (real UUIDs, so the eventual proposal snapshot validates), and
 * never hits the network. When not suggesting, it delegates to the real
 * mutation unchanged.
 *
 * Wrapping the WHOLE set in one place means a new edit mutation added later
 * fails the type-check here until it gets a shim — it can't silently write to
 * the live draft.
 *
 * @module FlowDesigner/proposals/stagedMutations
 */

import type { MutableRefObject } from 'react';
import type { AckResult, DesignerMutations, MutationLike } from '../mutationContracts';

export type {
  AckResult,
  CreateEdgeResult,
  CreateNodeResult,
  DesignerMutations,
  InsertNodeOnEdgeResult,
  MutationLike,
} from '../mutationContracts';

/** Generate a real UUID for a staged node/edge so the snapshot validates. */
function uuid(): string {
  return crypto.randomUUID();
}

/**
 * Wrap one mutation so it resolves locally (via `makeResult`) while suggesting,
 * delegating to the real mutation otherwise. Other reactive fields (isPending…)
 * are preserved at runtime through the spread; only `mutateAsync` is replaced.
 */
function stage<TVars, TResult>(
  real: MutationLike<TVars, TResult>,
  suggestingRef: MutableRefObject<boolean>,
  makeResult: (vars: TVars) => TResult
): MutationLike<TVars, TResult> {
  return {
    ...real,
    mutateAsync: (vars: TVars): Promise<TResult> =>
      suggestingRef.current ? Promise.resolve(makeResult(vars)) : real.mutateAsync(vars),
  };
}

const ack = (): AckResult => ({ success: true });

/**
 * Produce a parallel set of mutations that stage locally while suggesting.
 * The synthetic responses mirror exactly the fields each handler reads:
 *   - create_node           → `{ node: { id } }`
 *   - create_edge           → `{ edge: { id } }`
 *   - insert_node_on_edge    → `{ node, sourceEdge, targetEdge }`
 *   - everything else        → `{ success: true }` (response unused by callers)
 */
export function makeStagedMutations(
  real: DesignerMutations,
  suggestingRef: MutableRefObject<boolean>
): DesignerMutations {
  return {
    createNodeMutation: stage(real.createNodeMutation, suggestingRef, () => ({
      node: { id: uuid() },
    })),
    createEdgeMutation: stage(real.createEdgeMutation, suggestingRef, () => ({
      edge: { id: uuid() },
    })),
    insertNodeOnEdgeMutation: stage(real.insertNodeOnEdgeMutation, suggestingRef, () => ({
      node: { id: uuid() },
      sourceEdge: { id: uuid() },
      targetEdge: { id: uuid() },
    })),
    updateNodeMutation: stage(real.updateNodeMutation, suggestingRef, ack),
    deleteNodeMutation: stage(real.deleteNodeMutation, suggestingRef, ack),
    bulkDeleteNodesMutation: stage(real.bulkDeleteNodesMutation, suggestingRef, ack),
    deleteEdgeMutation: stage(real.deleteEdgeMutation, suggestingRef, ack),
    syncDraftStateMutation: stage(real.syncDraftStateMutation, suggestingRef, ack),
  };
}
